/**
 * Centralized UI labels — single source of truth for all user-facing text.
 *
 * Import from here — never hardcode display labels in components.
 * When adding translations, replace string values with a lookup function
 * that reads from the active locale (e.g. labels.en.ts, labels.pt.ts).
 */

// ── Setup Type ──────────────────────────────────────────────────────────────
// Internal values: "major" | "minor"
// Display: "New" | "Repeat"

export const SETUP_TYPE_LABELS: Record<string, string> = {
  major: "New",
  minor: "Repeat",
};

export const SETUP_TYPE_TOOLTIPS: Record<string, string> = {
  major: "New — full setup (plates, dies, registration from scratch)",
  minor: "Repeat — reduced setup for repeat jobs where tooling already exists",
};

export function setupTypeLabel(value?: string | null): string {
  return SETUP_TYPE_LABELS[value || "major"] || SETUP_TYPE_LABELS.major;
}

// ── Quotation Status ────────────────────────────────────────────────────────

export const QUOTATION_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_calc: "Pending Calc",
  ready_for_proposal: "Ready",
  submitted: "Submitted",
  proposal_sent: "Proposal Sent",
  proposal_accepted: "Accepted",
  proposal_rejected: "Rejected",
  approved: "Approved",
  rejected: "Rejected",
  on_hold: "On Hold",
  completed: "Completed",
  cancelled: "Cancelled",
};

// ── Proposal Status ─────────────────────────────────────────────────────────

export const PROPOSAL_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  accepted: "Accepted",
  rejected: "Rejected",
};

export const PROPOSAL_ACTION_LABELS: Record<string, string> = {
  send: "Send",
  accept: "Accept",
  reject: "Reject",
};

// ── Order Status ────────────────────────────────────────────────────────────

export const ORDER_STATUS_LABELS: Record<string, string> = {
  confirmed: "Confirmed",
  in_production: "In Production",
  shipped: "Shipped",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const ORDER_ACTION_LABELS: Record<string, string> = {
  in_production: "Start Production",
  shipped: "Mark Shipped",
  completed: "Mark Completed",
  cancelled: "Cancel Order",
};

// ── Bulk Upload Status ──────────────────────────────────────────────────────

export const BULK_STATUS_LABELS: Record<string, string> = {
  calculated: "Calculated",
  created: "Created",
  error: "Error",
  pending: "Pending",
};

// ── Navigation ──────────────────────────────────────────────────────────────

export const NAV_LABELS = {
  quotations: "Quotations",
  proposals: "Proposals",
  orders: "Orders",
  customers: "Customers",
  machines: "Machines",
  materials: "Materials",
  profiles: "Profiles",
  settings: "Settings",
} as const;

// ── Page Headers ────────────────────────────────────────────────────────────

export const PAGE_HEADERS = {
  quotations: { title: "Quotations", subtitle: "Create, search, and manage estimates" },
  proposals: { title: "Proposals", subtitle: "Commercial proposals with margin and pricing" },
  orders: { title: "Orders", subtitle: "Production orders from accepted proposals" },
  customers: { title: "Customers", subtitle: "Manage customer accounts" },
  machines: { title: "Machines", subtitle: "Machine configuration and cost versions" },
  materials: { title: "Materials", subtitle: "Material grades, GSM options, and pricing" },
  profiles: { title: "Carton Profiles", subtitle: "Die-cut profiles, dimension rules, process and finish catalogs" },
} as const;

// ── Wizard Steps ────────────────────────────────────────────────────────────

export const WIZARD_STEP_LABELS = {
  header: "Quote Setup",
  profile: "Profile",
  dimensions: "Dimensions",
  material: "Material",
  options: "Options",
  colors: "Colors & Varnish",
  finishes: "Extras",
  quantities: "Quantities",
  route: "Process Route",
  costs: "Cost Breakdown",
  detail: "Detail",
} as const;

// ── Common Actions ──────────────────────────────────────────────────────────

export const ACTION_LABELS = {
  save: "Save",
  cancel: "Cancel",
  back: "Back",
  next: "Next",
  edit: "Edit",
  view: "View",
  clone: "Clone",
  delete: "Delete",
  export: "Export",
  calculate: "Calculate",
  saveDraft: "Save Draft",
  saving: "Saving...",
  draftSaved: "Draft Saved",
  newEstimate: "New Estimate (Guided Wizard)",
  bulkUpload: "Bulk Upload",
  hideBulkUpload: "Hide Bulk Upload",
  downloadTemplate: "Download Template",
  uploadTemplate: "Upload Filled Template",
  uploading: "Uploading...",
  newQuotation: "New Quotation",
  newProposal: "+ New Proposal",
  createOrder: "+ Create Order",
  quickAdd: "+ Quick Add",
} as const;

// ── Finish Labels ───────────────────────────────────────────────────────────

export const FINISH_LABELS: Record<string, string> = {
  "FIN-GLOSS-LAM": "Gloss Lam",
  "FIN-MATT-LAM": "Matt Lam",
  "FIN-SOFT-TOUCH": "Soft Touch",
  "FIN-SPOT-UV": "Spot UV",
  "FIN-FLOOD-UV": "Flood UV",
  "FIN-AQ-GLOSS": "AQ Gloss",
  "FIN-AQ-MATT": "AQ Matt",
  "FIN-HOT-FOIL-G": "Foil Gold",
  "FIN-HOT-FOIL-S": "Foil Silver",
  "FIN-HOT-FOIL-C": "Foil Custom",
  "FIN-EMBOSS": "Emboss",
  "FIN-DEBOSS": "Deboss",
  "FIN-WINDOW-PET": "Window PET",
  "FIN-WINDOW-BIO": "Window Bio",
};

// ── Worksheet Column Labels ─────────────────────────────────────────────────
// Used by ProposalWorksheet for column headers

export const WORKSHEET_LABELS: Record<string, string> = {
  option_no: "Opt",
  quantity: "Qty",
  setup_type: "Run Type",
  sorts: "Sorts",
  colors_display: "Colors",
  varnish_display: "Varnish",
  quote_cost: "Quote Cost",
  finishes_summary: "Finishes",
  nm_pct: "NM %",
  nm_run_on_pct: "NM RO %",
  gm_pct: "GM %",
  gm_run_on_pct: "GM RO %",
  sell_price: "Total Price",
  price_per_1000: "Price/1000",
  run_on_price_per_1000: "RO/1000",
  setup_cost: "Total Setup",
  major_setup_cost: "Major Setup",
  minor_setup_cost: "Minor Setup",
  annual_volume: "Ann Volume",
  annual_orders: "Orders/yr",
  annual_value: "Annual Val",
  nm_value: "NM Value",
  gm_value: "GM Value",
  added_value: "Added Val",
  added_value_pct: "AV %",
  ebitda: "EBITDA",
  ebitda_pct: "EBITDA %",
  spread: "Spread",
  spread_pct: "Spread %",
  contribution: "Contrib",
  contribution_pct: "Contrib %",
  ksm: "KSM",
  annual_nm_value: "Ann NM",
  annual_gm_value: "Ann GM",
  annual_added_value: "Ann AV",
  annual_ebitda: "Ann EBITDA",
  annual_spread: "Ann Spread",
  annual_contribution: "Ann Contrib",
  commission_pct: "Comm %",
  overhead_pct: "OH %",
  internal_comment: "Internal Comment",
};

// ── Table Column Headers ────────────────────────────────────────────────────

export const TABLE_HEADERS = {
  reference: "Reference",
  customer: "Customer",
  overview: "Overview",
  estimator: "Estimator",
  status: "Status",
  currency: "CCY",
  created: "Created",
  orderRef: "Order Ref",
  amount: "Amount",
  confirmed: "Confirmed",
  quotation: "Quotation",
  code: "Code",
  name: "Name",
  description: "Description",
} as const;
